import React, { Component } from 'react';
import { translate } from 'react-i18next';
import { Menu } from 'primereact/menu';
import { getStyle, styleMerge } from '../../utils/utils';
import CBMediaQuery from './CBMediaQuery';
import CBLabelAndCheckbox from './CBLabelAndCheckbox';
import CBFieldsDialog from './CBFieldsDialog';

// Reusable dark-theme mobile card that replaces a horizontal-scroll table row on
// small screens. Shows a hand-picked subset of fields on the face; a three-dot menu
// exposes per-row actions plus a built-in "show all fields" dialog so the full set
// of desktop columns stays reachable. Supports an optional checkbox (bulk select)
// and an optional collapsible accordion body (e.g. chain parts vs other parts).
//
// Props:
//   leadingIcon       optional '/img/*.svg' shown in a rounded badge
//   title             node (e.g. reg-number <Link>)
//   titleControl      optional interactive title; owns its own navigation, outside the card click target
//   dialogTitle       string used as the "show all fields" dialog header
//   subtitle          node (e.g. manufacturer + model)
//   badges            optional array of nodes (status / approval / priority chips)
//   fields            [{ label, value, valueColor }] curated face rows
//   options           primereact Menu model [{ label, icon, command|url }] (per-row actions)
//   allFields         [{ label, value }] full column set for the dialog
//   expandableContent optional node rendered when the card is expanded
//   expandLabel       string label for the expand toggle
//   onExpand          optional callback fired once when the card is expanded (lazy fetch)
//   selected          bool (checkbox state)
//   onSelectChange    optional fn -> renders a per-card checkbox
//   onClick           optional fn fired when tapping the title / fields area
class CBMobileCard extends Component {
  constructor(props) {
    super(props);
    this.state = {
      expanded: false,
      fieldsDialogOpen: false,
    };
    this.optionsMenu = React.createRef();
  }

  toggleExpanded = (e) => {
    if (e) { e.stopPropagation(); }
    const willExpand = !this.state.expanded;
    this.setState({ expanded: willExpand });
    if (willExpand && this.props.onExpand) {
      this.props.onExpand();
    }
  };

  openFieldsDialog = () => {
    this.setState({ fieldsDialogOpen: true });
  };

  closeFieldsDialog = () => {
    this.setState({ fieldsDialogOpen: false });
  };

  getMenuModel = () => {
    const { options, allFields, t } = this.props;
    const model = (options || []).slice();
    if (allFields && allFields.length > 0) {
      model.push({
        label: t('showAllFields', { ns: 'common' }),
        icon: 'pi pi-list',
        command: () => { this.openFieldsDialog(); },
      });
    }
    return model;
  };

  handleBodyClick = () => {
    const { onClick } = this.props;
    if (onClick) { onClick(); }
  };

  render() {
    const {
      leadingIcon,
      title,
      titleControl,
      dialogTitle,
      subtitle,
      badges,
      fields,
      allFields,
      expandableContent,
      expandLabel,
      selected,
      onSelectChange,
      t,
    } = this.props;
    const { expanded, fieldsDialogOpen } = this.state;
    const menuModel = this.getMenuModel();
    const hasMenu = menuModel.length > 0;

    return (
      <CBMediaQuery>
        {screenSize => (
          <div style={getStyle(screenSize, styles, 'card')}>
            <div style={getStyle(screenSize, styles, 'headerRow')}>
              {onSelectChange && (
                <div
                  style={getStyle(screenSize, styles, 'checkboxWrap')}
                  onClick={(e) => { e.stopPropagation(); }}
                >
                  <CBLabelAndCheckbox checked={selected} onChange={onSelectChange} />
                </div>
              )}
              {leadingIcon && (
                <div style={getStyle(screenSize, styles, 'leadingIconWrap')}>
                  <img src={leadingIcon} alt='' style={getStyle(screenSize, styles, 'leadingIcon')} />
                </div>
              )}
              <div style={getStyle(screenSize, styles, 'titleWrap')} onClick={titleControl ? undefined : this.handleBodyClick}>
                <div style={getStyle(screenSize, styles, 'title')}>{titleControl || title}</div>
                {subtitle && (
                  <div style={getStyle(screenSize, styles, 'subtitle')}>{subtitle}</div>
                )}
              </div>
              {hasMenu && (
                <div style={getStyle(screenSize, styles, 'optionsWrap')}>
                  <Menu model={menuModel} popup ref={this.optionsMenu} />
                  <div
                    onClick={(e) => { e.stopPropagation(); this.optionsMenu.current.toggle(e); }}
                  >
                    <img
                      src='/img/icon_options.svg'
                      alt='Options'
                      className='opacity-and-border-on-hover'
                      style={getStyle(screenSize, styles, 'optionsIcon')}
                    />
                  </div>
                </div>
              )}
            </div>

            {badges && badges.length > 0 && (
              <div style={getStyle(screenSize, styles, 'badgesRow')}>
                {badges.map((badge, idx) => (
                  <React.Fragment key={idx}>{badge}</React.Fragment>
                ))}
              </div>
            )}

            {fields && fields.length > 0 && (
              <div style={getStyle(screenSize, styles, 'fieldsWrap')} onClick={this.handleBodyClick}>
                {fields.map((field, idx) => (
                  <div key={idx} style={getStyle(screenSize, styles, 'fieldRow')}>
                    <span style={getStyle(screenSize, styles, 'fieldLabel')}>{field.label}</span>
                    <span
                      style={styleMerge(
                        getStyle(screenSize, styles, 'fieldValue'),
                        field.valueColor ? { color: field.valueColor } : {},
                      )}
                    >
                      {field.value}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {expandableContent && (
              <div style={getStyle(screenSize, styles, 'expandSection')}>
                <div style={getStyle(screenSize, styles, 'expandToggle')} onClick={this.toggleExpanded}>
                  <img
                    src={expanded ? '/img/up_arrow_icon.svg' : '/img/down_arrow_icon.svg'}
                    alt=''
                    style={getStyle(screenSize, styles, 'expandIcon')}
                  />
                  <span style={getStyle(screenSize, styles, 'expandLabel')}>
                    {expandLabel || t('showMore', { ns: 'common' })}
                  </span>
                </div>
                {expanded && (
                  <div style={getStyle(screenSize, styles, 'expandBody')}>
                    {expandableContent}
                  </div>
                )}
              </div>
            )}

            {allFields && allFields.length > 0 && (
              <CBFieldsDialog
                modalIsOpen={fieldsDialogOpen}
                closeModal={this.closeFieldsDialog}
                title={dialogTitle}
                fields={allFields}
              />
            )}
          </div>
        )}
      </CBMediaQuery>
    );
  }
}

const styles = {
  default: {
    card: {
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      border: '1px solid rgba(255, 255, 255, 0.18)',
      borderRadius: 10,
      padding: 14,
      marginBottom: 12,
    },
    headerRow: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: 10,
    },
    checkboxWrap: {
      paddingTop: 4,
      flexShrink: 0,
    },
    leadingIconWrap: {
      flexShrink: 0,
      width: 36,
      height: 36,
      borderRadius: 8,
      backgroundColor: 'rgba(0, 149, 218, 0.18)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    leadingIcon: {
      width: 20,
      height: 20,
    },
    titleWrap: {
      flex: 1,
      minWidth: 0,
      cursor: 'pointer',
    },
    title: {
      color: '#FFFFFF',
      fontSize: 22,
      fontFamily: 'TitilliumWeb-Bold',
      lineHeight: 1.15,
      wordBreak: 'break-word',
    },
    subtitle: {
      color: 'rgba(255, 255, 255, 0.7)',
      fontSize: 14,
      fontFamily: 'TitilliumWeb-Regular',
      marginTop: 2,
    },
    optionsWrap: {
      flexShrink: 0,
      marginLeft: 'auto',
    },
    optionsIcon: {
      width: 24,
      height: 24,
      cursor: 'pointer',
      padding: 4,
    },
    badgesRow: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
      marginTop: 10,
    },
    fieldsWrap: {
      marginTop: 12,
      borderTop: '1px solid rgba(255, 255, 255, 0.12)',
      paddingTop: 6,
      cursor: 'pointer',
    },
    fieldRow: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: 12,
      padding: '4px 0',
    },
    fieldLabel: {
      color: 'rgba(255, 255, 255, 0.6)',
      fontSize: 13,
      fontFamily: 'TitilliumWeb-Regular',
      flexShrink: 0,
    },
    fieldValue: {
      color: '#FFFFFF',
      fontSize: 15,
      fontFamily: 'TitilliumWeb-SemiBold',
      textAlign: 'right',
      wordBreak: 'break-word',
    },
    expandSection: {
      marginTop: 10,
      borderTop: '1px solid rgba(255, 255, 255, 0.12)',
      paddingTop: 4,
    },
    expandToggle: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      cursor: 'pointer',
      padding: '6px 0',
    },
    expandIcon: {
      width: 14,
      height: 14,
      opacity: 0.6,
    },
    expandLabel: {
      color: '#80D8FF',
      fontSize: 14,
      fontFamily: 'TitilliumWeb-SemiBold',
    },
    expandBody: {
      marginTop: 8,
    },
  },
};

export default translate('common')(CBMobileCard);
